import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import {
  Video,
  Users,
  Clock,
  Play,
  Square,
  Trash2,
  Eye,
  Radio,
  Calendar,
  Bot,
  Loader2,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { EmptyState } from '@/components/shared/EmptyState';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { DataTable } from '@/components/shared/DataTable';
import { JsonViewer } from '@/components/shared/JsonViewer';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import {
  useGetMeetingsQuery,
  useCreateMeetingMutation,
  useStartMeetingMutation,
  useEndMeetingMutation,
  useDeleteMeetingMutation,
  useJoinAgentMutation,
  useLeaveAgentMutation,
  useUnsubscribeFromMeetingMutation,
} from '@/store/api/apiSlice';
import type { Meeting } from '@/types';
import { format, formatDistanceToNow } from 'date-fns';
import { wsClient } from '@/lib/ws';
import { MeetingEventType, MEETINGS_UPDATES_ROOM } from '@cluster/types';

const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'pending', label: 'Pending' },
  { value: 'paused', label: 'Paused' },
  { value: 'ended', label: 'Ended' },
];

function truncateUrl(url: string, maxLen = 32): string {
  if (url.length <= maxLen) return url;
  return url.slice(0, maxLen) + '…';
}

const MEET_PATH_PATTERN = /^\/[a-z]{3}-[a-z]{4}-[a-z]{3}$/i;

/**
 * Normalize a user-entered Google Meet URL into a canonical `https://` form.
 *
 * - trims surrounding whitespace;
 * - upgrades an explicit `http://` scheme to `https://`;
 * - prefixes `https://` when no scheme is present but the value starts with
 *   `meet.google.com/`;
 * - strips trailing slashes from the path;
 * - lowercases the hostname and drops a leading `www.`;
 * - preserves query params and hash untouched.
 *
 * Returns the input (trimmed) unchanged when it cannot be parsed as a URL —
 * callers must still validate the result with {@link isValidMeetUrl}.
 */
export function normalizeMeetUrl(raw: string): string {
  let value = raw.trim();

  if (/^http:\/\//i.test(value)) {
    value = value.replace(/^http:\/\//i, 'https://');
  } else if (/^meet\.google\.com\//i.test(value)) {
    value = `https://${value}`;
  }

  try {
    const parsed = new URL(value);
    parsed.hostname = parsed.hostname.replace(/^www\./i, '');
    parsed.pathname = parsed.pathname.replace(/\/+$/, '');
    return parsed.toString();
  } catch {
    return value;
  }
}

/**
 * Validate a normalized Google Meet URL: must be `https:`, hosted on
 * `meet.google.com`, with a `xxx-yyyy-zzz` meeting code path. Query params and
 * hash are ignored.
 */
export function isValidMeetUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    return (
      parsed.protocol === 'https:' &&
      parsed.hostname === 'meet.google.com' &&
      MEET_PATH_PATTERN.test(parsed.pathname)
    );
  } catch {
    return false;
  }
}

export function MeetingsPage() {
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Meeting | null>(null);

  // Single query: fetch all meetings (WebSocket-driven refresh)
  const { data: meetingsData, isLoading, isFetching, isError, error, refetch } = useGetMeetingsQuery();

  // Use ref to access refetch without causing useEffect re-runs
  const refetchRef = useRef(refetch);
  useEffect(() => {
    refetchRef.current = refetch;
  }, [refetch]);

  const [startMeeting, { isLoading: isStarting }] = useStartMeetingMutation();
  const [endMeeting, { isLoading: isEnding }] = useEndMeetingMutation();
  const [deleteMeeting, { isLoading: isDeleting }] = useDeleteMeetingMutation();
  const [createMeeting, { isLoading: isCreating }] = useCreateMeetingMutation();
  const [joinAgent, { isLoading: isJoining }] = useJoinAgentMutation();
  const [leaveAgent] = useLeaveAgentMutation();
  const [unsubscribeFromMeeting, { isLoading: isUnsubscribing }] = useUnsubscribeFromMeetingMutation();

  // WebSocket real-time updates
  useEffect(() => {
    const onMeetingEvent = () => { refetchRef.current(); };

    const unsubConnected = wsClient.on('connected', () => {
      wsClient.subscribe(MEETINGS_UPDATES_ROOM).catch(() => {});
    });

    if (!wsClient.connected) {
      wsClient.connect().catch(() => {});
    } else {
      wsClient.subscribe(MEETINGS_UPDATES_ROOM).catch(() => {});
    }

    const unsubs = [
      unsubConnected,
      wsClient.on(MeetingEventType.CREATED, onMeetingEvent),
      wsClient.on(MeetingEventType.UPDATED, onMeetingEvent),
      wsClient.on(MeetingEventType.STARTED, onMeetingEvent),
      wsClient.on(MeetingEventType.ENDED, onMeetingEvent),
    ];

    return () => {
      unsubs.forEach((u) => u());
      wsClient.unsubscribe(MEETINGS_UPDATES_ROOM).catch(() => {});
    };
  }, []);

  // Launch Bot modal state
  const [isLaunchOpen, setIsLaunchOpen] = useState(false);
  const [launchForm, setLaunchForm] = useState({
    meetingUrl: '',
    title: '',
    botName: 'Bot',
  });
  const [launchError, setLaunchError] = useState<string | null>(null);
  const [launchSuccess, setLaunchSuccess] = useState(false);

  // Synchronous in-flight guard for the launch flow. A ref is used because
  // React state updates are async — two rapid clicks could both pass an
  // `isLaunching` state check before the button re-renders as disabled.
  const launchInFlightRef = useRef(false);
  const [isLaunchPending, setIsLaunchPending] = useState(false);

  // Disable the Launch button for the whole flow (createMeeting + joinAgent).
  const isLaunching = isCreating || isJoining || isLaunchPending;

  // Derive the full list from the single query response
  const fullList = (meetingsData as Meeting[] | undefined) ?? [];

  // Filter client-side based on the status tab
  const meetingList = useMemo(() => {
    if (statusFilter === 'all') return fullList;
    return fullList.filter((m) => m.status === statusFilter);
  }, [fullList, statusFilter]);

  // Stats computed from the full unfiltered list
  const stats = useMemo(() => {
    return {
      total: fullList.length,
      active: fullList.filter((m) => m.status === 'active').length,
      pending: fullList.filter((m) => m.status === 'pending').length,
      ended: fullList.filter((m) => m.status === 'ended').length,
    };
  }, [fullList]);

  const handleLaunch = useCallback(async () => {
    // Synchronous guard: bail out if a launch is already in progress. This
    // closes the window between a double-click and the button re-rendering
    // as disabled, preventing duplicate meetings/bots.
    if (launchInFlightRef.current) return;

    const url = normalizeMeetUrl(launchForm.meetingUrl);
    if (!url) return;

    // Validate the normalized Google Meet URL before sending to backend
    if (!isValidMeetUrl(url)) {
      setLaunchError('Invalid Google Meet URL. Format: https://meet.google.com/xxx-yyyy-zzz');
      return;
    }

    setLaunchError(null);
    setLaunchSuccess(false);
    launchInFlightRef.current = true;
    setIsLaunchPending(true);
    try {
      const meetingId = `mtg_${Date.now()}`;
      // 1. Create meeting record
      await createMeeting({
        meetingId,
        meetingUrl: url,
        title: launchForm.title.trim() || undefined,
      } as unknown as Partial<Meeting>).unwrap();
      // 2. Launch bot to join the meeting
      await joinAgent({
        meetingId,
        meetingUrl: url,
        botName: launchForm.botName.trim() || 'Bot',
      }).unwrap();
      // 3. Success — reset and close
      setLaunchSuccess(true);
      setLaunchForm({ meetingUrl: '', title: '', botName: 'Bot' });
      setTimeout(() => {
        setIsLaunchOpen(false);
        setLaunchSuccess(false);
      }, 1500);
    } catch (err: unknown) {
      let message = 'Failed to launch bot. Please try again.';

      if (err && typeof err === 'object') {
        // RTK Query error format
        if ('data' in err) {
          const data = (err as { data?: unknown }).data;

          // Backend API error format: { error: { code, message, details, requestId } }
          if (data && typeof data === 'object' && 'error' in data) {
            const errorObj = (data as { error?: unknown }).error;
            if (errorObj && typeof errorObj === 'object') {
              if ('message' in errorObj && typeof (errorObj as { message: unknown }).message === 'string') {
                message = (errorObj as { message: string }).message;
              } else if ('details' in errorObj && Array.isArray((errorObj as { details: unknown[] }).details)) {
                message = (errorObj as { details: Array<{ message?: string } | string> }).details
                  .map((d) => (typeof d === 'string' ? d : d?.message ?? ''))
                  .filter(Boolean)
                  .join(', ');
              }
            } else if (typeof errorObj === 'string') {
              message = errorObj;
            }
          } else if ('message' in (data as object) && typeof (data as { message: unknown }).message === 'string') {
            // Direct message field: { message: "..." }
            message = (data as { message: string }).message;
          } else if (typeof data === 'string') {
            // Plain string error
            message = data;
          }
        } else if ('error' in err) {
          // Network error or fetch error
          const error = (err as { error?: unknown }).error;
          if (typeof error === 'string') {
            message = error;
          }
        }
      }

      setLaunchError(message);
    } finally {
      launchInFlightRef.current = false;
      setIsLaunchPending(false);
    }
  }, [launchForm, createMeeting, joinAgent]);

  const handleStartWithBot = useCallback(
    async (meeting: Meeting) => {
      try {
        await startMeeting(meeting.meetingId).unwrap();
        await joinAgent({
          meetingId: meeting.meetingId,
          meetingUrl: meeting.meetingUrl,
          botName: meeting.botName || meeting.metadata?.title || 'Bot',
        }).unwrap();
      } catch {
        // startMeeting already invalidates cache; joinAgent failure is non-fatal
      }
    },
    [startMeeting, joinAgent],
  );

  const handleEnd = useCallback(
    async (meeting: Meeting) => {
      try {
        // First instruct the bot to leave (best-effort, don't block if it fails)
        if (meeting.botId) {
          await leaveAgent({ meetingId: meeting.meetingId }).unwrap().catch((err) => {
            console.warn('Failed to leave agent:', err);
          });
        }
        // Then end the meeting
        await endMeeting(meeting.meetingId).unwrap();
      } catch {
        // endMeeting invalidates cache on success; errors are non-fatal for the UI
      }
    },
    [leaveAgent, endMeeting],
  );

  const handleUnsubscribe = useCallback(
    async (subjectId: string) => {
      if (!selectedMeeting) return;
      try {
        // DELETE /api/meetings/:meetingId/subscribe/:subjectId — real endpoint
        await unsubscribeFromMeeting({
          meetingId: selectedMeeting.meetingId,
          subjectId,
        }).unwrap();
      } catch (err) {
        console.warn('Failed to unsubscribe subscriber:', err);
      }
    },
    [selectedMeeting, unsubscribeFromMeeting],
  );

  // Keep the detail dialog in sync with fresh list data (mutations + WS refresh)
  const activeMeeting = selectedMeeting
    ? fullList.find((m) => m.meetingId === selectedMeeting.meetingId) ?? selectedMeeting
    : null;

  const columns: ColumnDef<Meeting, unknown>[] = [
    {
      accessorKey: 'meetingId',
      header: 'Meeting ID',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Video className="h-4 w-4 text-muted-foreground shrink-0" />
          <span className="font-mono text-xs font-medium">{row.original.meetingId}</span>
        </div>
      ),
    },
    {
      accessorKey: 'meetingUrl',
      header: 'URL',
      cell: ({ row }) => (
        <a
          href={row.original.meetingUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-blue-600 dark:text-blue-400 hover:underline font-mono"
          title={row.original.meetingUrl}
        >
          {truncateUrl(row.original.meetingUrl)}
        </a>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
    },
    {
      accessorKey: 'botId',
      header: 'Bot',
      cell: ({ row }) =>
        row.original.botId ? (
          <Badge variant="secondary" className="font-mono text-xs">
            {row.original.botId}
          </Badge>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    {
      accessorKey: 'participants',
      header: 'Participants',
      cell: ({ row }) => (
        <Badge variant="outline" className="gap-1">
          <Users className="h-3 w-3" />
          {row.original.participants.length}
        </Badge>
      ),
    },
    {
      accessorKey: 'subscribers',
      header: 'Subscribers',
      cell: ({ row }) => (
        <Badge variant="outline" className="gap-1">
          <Radio className="h-3 w-3" />
          {row.original.subscribers.length}
        </Badge>
      ),
    },
    {
      accessorKey: 'startedAt',
      header: 'Started',
      cell: ({ row }) =>
        row.original.startedAt
          ? formatDistanceToNow(new Date(row.original.startedAt), { addSuffix: true })
          : '—',
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const meeting = row.original;
        const isActive = meeting.status === 'active';
        const isPending = meeting.status === 'pending';
        const canStart = isPending;
        const canEnd = isActive;

        return (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setSelectedMeeting(meeting)}
              title="View details"
            >
              <Eye className="h-4 w-4" />
            </Button>
            {canStart && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-green-600 hover:text-green-700"
                onClick={() => handleStartWithBot(meeting)}
                disabled={isStarting || isJoining}
                title="Start meeting & launch bot"
              >
                <Play className="h-4 w-4" />
              </Button>
            )}
            {canEnd && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-orange-600 hover:text-orange-700"
                onClick={() => handleEnd(meeting)}
                disabled={isEnding}
                title="End meeting"
              >
                <Square className="h-4 w-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-destructive hover:text-destructive"
              onClick={() => setDeleteTarget(meeting)}
              title="Delete meeting"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Meetings</h1>
          <p className="text-sm text-muted-foreground">
            Manage Google Meeting sessions and subscriptions
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => { setIsLaunchOpen(true); setLaunchError(null); setLaunchSuccess(false); }}>
            <Bot className="mr-2 h-4 w-4" />
            Launch Bot
          </Button>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total"
          value={stats.total}
          icon={<Video className="h-4 w-4 text-muted-foreground" />}
        />
        <StatCard
          title="Active"
          value={stats.active}
          icon={<Radio className="h-4 w-4 text-green-500" />}
          accent="text-green-600 dark:text-green-400"
        />
        <StatCard
          title="Pending"
          value={stats.pending}
          icon={<Clock className="h-4 w-4 text-yellow-500" />}
          accent="text-yellow-600 dark:text-yellow-400"
        />
        <StatCard
          title="Ended"
          value={stats.ended}
          icon={<Square className="h-4 w-4 text-gray-500" />}
          accent="text-gray-600 dark:text-gray-400"
        />
      </div>

      {/* Filters */}
      <Tabs value={statusFilter} onValueChange={setStatusFilter}>
        <TabsList>
          {STATUS_FILTERS.map((f) => (
            <TabsTrigger key={f.value} value={f.value}>
              {f.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Data table */}
      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <LoadingSpinner className="py-12" />
          ) : isError ? (
            <div className="text-center py-8 text-destructive">
              <p>Failed to load meetings{(error as { data?: { message?: string } })?.data?.message ? `: ${(error as { data?: { message?: string } }).data?.message}` : ''}</p>
            </div>
          ) : meetingList.length === 0 ? (
            <EmptyState
              title="No meetings found"
              description="Meetings will appear here once they are created"
              icon={<Video className="h-8 w-8" />}
            />
          ) : (
            <>
              <div
                className={`text-xs text-muted-foreground text-center py-2 transition-opacity ${isFetching ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                style={{ minHeight: '1.5rem' }}
              >
                Refreshing...
              </div>
              <DataTable columns={columns} data={meetingList} />
            </>
          )}
        </CardContent>
      </Card>

      {/* Detail dialog */}
      <Dialog open={!!selectedMeeting} onOpenChange={(open) => !open && setSelectedMeeting(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Video className="h-5 w-5" />
              Meeting Details
            </DialogTitle>
            <DialogDescription className="font-mono text-xs">
              {selectedMeeting?.meetingId}
            </DialogDescription>
          </DialogHeader>

          {activeMeeting && (
            <MeetingDetail
              meeting={activeMeeting}
              onUnsubscribe={handleUnsubscribe}
              isUnsubscribing={isUnsubscribing}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete Meeting"
        description={`Are you sure you want to delete meeting "${deleteTarget?.meetingId}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={() => {
          if (deleteTarget) {
            deleteMeeting(deleteTarget.meetingId);
            setDeleteTarget(null);
          }
        }}
        isLoading={isDeleting}
      />

      {/* Launch Bot dialog */}
      <Dialog
        open={isLaunchOpen}
        onOpenChange={(open) => {
          setIsLaunchOpen(open);
          if (!open) {
            setLaunchError(null);
            setLaunchSuccess(false);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bot className="h-5 w-5" />
              Launch Bot
            </DialogTitle>
            <DialogDescription>
              Enter a Google Meet URL to create a meeting and launch the bot.
            </DialogDescription>
          </DialogHeader>

          {/* Feedback messages */}
          {launchSuccess && (
            <div className="flex items-center gap-2 rounded-md bg-green-50 p-3 text-sm text-green-700 dark:bg-green-950 dark:text-green-300">
              <CheckCircle2 className="h-4 w-4 shrink-0" />
              Bot launched successfully
            </div>
          )}
          {launchError && (
            <div className="flex items-center gap-2 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {launchError}
            </div>
          )}

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="meetingUrl">
                Meeting URL <span className="text-destructive">*</span>
              </Label>
              <Input
                id="meetingUrl"
                placeholder="https://meet.google.com/..."
                value={launchForm.meetingUrl}
                onChange={(e) => setLaunchForm((f) => ({ ...f, meetingUrl: e.target.value }))}
                disabled={isLaunching || launchSuccess}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="title">Title (optional)</Label>
              <Input
                id="title"
                placeholder="Meeting title"
                value={launchForm.title}
                onChange={(e) => setLaunchForm((f) => ({ ...f, title: e.target.value }))}
                disabled={isLaunching || launchSuccess}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="botName">Bot Name (optional)</Label>
              <Input
                id="botName"
                placeholder="Bot"
                value={launchForm.botName}
                onChange={(e) => setLaunchForm((f) => ({ ...f, botName: e.target.value }))}
                disabled={isLaunching || launchSuccess}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsLaunchOpen(false)}
              disabled={isLaunching}
            >
              Cancel
            </Button>
            <Button
              onClick={handleLaunch}
              disabled={isLaunching || !launchForm.meetingUrl.trim() || launchSuccess}
            >
              {isLaunching ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Launching...
                </>
              ) : (
                <>
                  <Bot className="mr-2 h-4 w-4" />
                  Launch
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Stat Card ──────────────────────────────────────────────────────────────

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  accent?: string;
}

function StatCard({ title, value, icon, accent }: StatCardProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent>
        <div className={`text-2xl font-bold ${accent ?? ''}`}>{value}</div>
      </CardContent>
    </Card>
  );
}

// ─── Meeting Detail ─────────────────────────────────────────────────────────

interface MeetingDetailProps {
  meeting: Meeting;
  onUnsubscribe?: (subjectId: string) => void;
  isUnsubscribing?: boolean;
}

function MeetingDetail({ meeting, onUnsubscribe, isUnsubscribing }: MeetingDetailProps) {
  return (
    <div className="space-y-6">
      {/* Overview grid */}
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <span className="text-muted-foreground">Status:</span>{' '}
          <StatusBadge status={meeting.status} />
        </div>
        <div>
          <span className="text-muted-foreground">Bot ID:</span>{' '}
          {meeting.botId ? (
            <span className="font-mono text-xs">{meeting.botId}</span>
          ) : (
            '—'
          )}
        </div>
        <div className="col-span-2">
          <span className="text-muted-foreground">URL:</span>{' '}
          <a
            href={meeting.meetingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 dark:text-blue-400 hover:underline font-mono text-xs"
          >
            {meeting.meetingUrl}
          </a>
        </div>
        <div>
          <span className="text-muted-foreground">Started:</span>{' '}
          {meeting.startedAt
            ? format(new Date(meeting.startedAt), 'MMM d, yyyy HH:mm')
            : '—'}
        </div>
        <div>
          <span className="text-muted-foreground">Ended:</span>{' '}
          {meeting.endedAt
            ? format(new Date(meeting.endedAt), 'MMM d, yyyy HH:mm')
            : '—'}
        </div>
        <div>
          <span className="text-muted-foreground">Created:</span>{' '}
          {format(new Date(meeting.createdAt), 'MMM d, yyyy HH:mm')}
        </div>
        <div>
          <span className="text-muted-foreground">Updated:</span>{' '}
          {format(new Date(meeting.updatedAt), 'MMM d, yyyy HH:mm')}
        </div>
      </div>

      {/* Participants */}
      <div>
        <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
          <Users className="h-4 w-4" />
          Participants ({meeting.participants.length})
        </h3>
        {meeting.participants.length > 0 ? (
          <div className="rounded-md border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-3 py-2 text-left font-medium">Name</th>
                  <th className="px-3 py-2 text-left font-medium">Email</th>
                  <th className="px-3 py-2 text-left font-medium">Role</th>
                </tr>
              </thead>
              <tbody>
                {meeting.participants.map((p, i) => (
                  <tr key={i} className="border-b last:border-0">
                    <td className="px-3 py-2 font-medium">{p.name}</td>
                    <td className="px-3 py-2 text-muted-foreground">{p.email ?? '—'}</td>
                    <td className="px-3 py-2">
                      <Badge variant="secondary" className="text-xs">
                        {p.role}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No participants</p>
        )}
      </div>

      {/* Subscribers */}
      <div>
        <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
          <Radio className="h-4 w-4" />
          Subscribers ({meeting.subscribers.length})
        </h3>
        {meeting.subscribers.length > 0 ? (
          <div className="rounded-md border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-3 py-2 text-left font-medium">Service</th>
                  <th className="px-3 py-2 text-left font-medium">Subject ID</th>
                  <th className="px-3 py-2 text-left font-medium">Event</th>
                  <th className="px-3 py-2 text-left font-medium">Subscribed</th>
                  {onUnsubscribe && (
                    <th className="px-3 py-2 text-left font-medium">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {meeting.subscribers.map((s, i) => (
                  <tr key={i} className="border-b last:border-0">
                    <td className="px-3 py-2 font-mono text-xs">{s.subscriberService}</td>
                    <td className="px-3 py-2 font-mono text-xs">{s.subjectId}</td>
                    <td className="px-3 py-2 text-muted-foreground">{s.eventName ?? '—'}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {format(new Date(s.subscribedAt), 'MMM d, HH:mm')}
                    </td>
                    {onUnsubscribe && (
                      <td className="px-3 py-2 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs text-destructive hover:text-destructive"
                          onClick={() => onUnsubscribe(s.subjectId)}
                          disabled={isUnsubscribing}
                          title={`Unsubscribe ${s.subjectId}`}
                        >
                          {isUnsubscribing ? (
                            <Loader2 className="h-3 w-3 animate-spin mr-1" />
                          ) : (
                            <Trash2 className="h-3 w-3 mr-1" />
                          )}
                          Unsubscribe
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No subscribers</p>
        )}
      </div>

      {/* Metadata */}
      {meeting.metadata && Object.keys(meeting.metadata).length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Metadata
          </h3>
          <JsonViewer data={meeting.metadata} />
        </div>
      )}
    </div>
  );
}
